import { differenceInDays, isAfter, isBefore, isEqual, parseISO } from 'date-fns'
import type { MissionFormValues } from '../../../missions/mission-form'
import type { WorkPeriodEditorFormErrors } from '../../work-periods-editor/work-period-editor'
import { validateWorkPeriodTheoreticalRange } from '../../validate-theoretical-range'
import { formatCompleteDate, formatISODate } from '../../../../utils/date'

/**
 * Formik values for inline mission work-period editing (calendar + week range in meta).
 */
export type InlineWorkPeriodsFormValues = {
  meta: { startDate: string; endDate: string }
  workPeriods: MissionFormValues['workPeriods']
}

export type InlineWorkPeriodsFormErrors = WorkPeriodEditorFormErrors & {
  meta?: { startDate?: string; endDate?: string }
}

/**
 * Validates meta date range and each work period (overlap, missing times, theoretical range).
 * Mirrors mission-form fixed-mode rules without worker availability (update context).
 */
export function validateInlineWorkPeriodsForm(
  values: InlineWorkPeriodsFormValues,
): InlineWorkPeriodsFormErrors {
  const errors: InlineWorkPeriodsFormErrors = {}

  const startDate = parseISO(values.meta.startDate)
  const endDate = parseISO(values.meta.endDate)

  if (isAfter(startDate, endDate)) {
    errors.meta = { ...errors.meta, startDate: 'La date de début doit être avant la date de fin' }
  }
  if (Math.abs(differenceInDays(startDate, endDate)) > 365) {
    errors.meta = { ...errors.meta, endDate: 'Une mission doit durer maximum 1 an' }
  }

  if (!values.workPeriods?.length) {
    errors.workPeriods = "Pas d'horaire de mission"
    return errors
  }

  for (let index = 0; index <= values.workPeriods.length; index++) {
    const workPeriod = values.workPeriods[index]
    const nextWorkPeriod = values.workPeriods[index + 1]
    const firstWorkPeriodStartDate =
      workPeriod && workPeriod.start ? workPeriod.start.date : undefined
    const firstWorkPeriodEndDate = workPeriod && workPeriod.end ? workPeriod.end.date : undefined
    const nextWorkPeriodStartDate =
      nextWorkPeriod && nextWorkPeriod.start ? nextWorkPeriod.start.date : undefined

    if (firstWorkPeriodStartDate && firstWorkPeriodEndDate && nextWorkPeriodStartDate) {
      if (
        isBefore(nextWorkPeriodStartDate, firstWorkPeriodEndDate) ||
        isEqual(firstWorkPeriodEndDate, nextWorkPeriodStartDate)
      ) {
        const niceDate = formatCompleteDate(firstWorkPeriodStartDate)
        const isoDate = formatISODate(firstWorkPeriodStartDate)
        const error = `${niceDate} : L'heure de fin ne peut être plus grande ou égale à l'heure du début du jour suivant`
        errors['workPeriods'] = error
        ;(errors[`workPeriod-${isoDate}`] ??= []).push(error)
      }
    }

    if (firstWorkPeriodStartDate === undefined && firstWorkPeriodEndDate) {
      const date = firstWorkPeriodStartDate ?? firstWorkPeriodEndDate
      const niceDate = formatCompleteDate(date)
      const isoDate = formatISODate(date)
      const error = `${niceDate} : L'heure de début n'est pas précisée`
      errors['workPeriods'] = error
      ;(errors[`workPeriod-${isoDate}`] ??= []).push(error)
    }
    if (firstWorkPeriodStartDate && firstWorkPeriodEndDate === undefined) {
      const date = firstWorkPeriodStartDate ?? firstWorkPeriodEndDate
      const niceDate = formatCompleteDate(date)
      const isoDate = formatISODate(date!)
      const error = `${niceDate} : L'heure de fin n'est pas précisée`
      errors['workPeriods'] = error
      ;(errors[`workPeriod-${isoDate}`] ??= []).push(error)
    }

    const rangeErrors =
      workPeriod &&
      validateWorkPeriodTheoreticalRange(workPeriod?.start?.date, workPeriod?.end?.date)
    if (rangeErrors && (rangeErrors.startDate || rangeErrors.endDate)) {
      const date = firstWorkPeriodStartDate ?? firstWorkPeriodEndDate
      const niceDate = date ? formatCompleteDate(date) : ''
      const isoDate = date ? formatISODate(date) : ''
      const error = `${niceDate} : ${rangeErrors.startDate ?? rangeErrors.endDate}`
      errors['workPeriods'] = error
      if (isoDate) (errors[`workPeriod-${isoDate}`] ??= []).push(error)
    }
  }

  return errors
}
